import React, { useEffect, useMemo, useState } from 'react';
import { TrendingUp, TrendingDown, ArrowLeftRight, ChevronDown, ChevronUp, CalendarClock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { fetchMarketDynamicsCommentary } from '@/lib/marketExperienceApi';
import { formatCategoryLabel } from '@/lib/marketExperience';
import { useTranslation } from '@/components/utils/translations';

/* KEY SECTOR TRENDS — first section of Market → Dynamics (2026-10-12).
 * The commentary is AI-composed from VERIFIED market-conditions research and stored server-side;
 * this component only renders it. No commentary for the tenant → renders nothing. */

const DIRECTION = {
  positive: { label: 'Favourable', labelKey: 'mktFavourable', cls: 'bg-[#E6F7F7] text-[#007A7A] border-[#B3E6E6]', Icon: TrendingUp },
  negative: { label: 'Unfavourable', labelKey: 'mktUnfavourable', cls: 'bg-[#FDECEC] text-[#B42318] border-[#F5C2C0]', Icon: TrendingDown },
  mixed: { label: 'Mixed', labelKey: 'mktMixed', cls: 'bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]', Icon: ArrowLeftRight },
  neutral: { label: 'Mixed', labelKey: 'mktMixed', cls: 'bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]', Icon: ArrowLeftRight },
};

function DirectionPill({ direction }) {
  const { t } = useTranslation();
  const d = DIRECTION[direction] || DIRECTION.neutral;
  const { Icon } = d;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${d.cls}`}>
      <Icon className="w-3 h-3" />
      {d.labelKey ? t(d.labelKey) : d.label}
    </span>
  );
}

function PointList({ title, items, tone }) {
  if (!items?.length) return null;
  return (
    <div>
      <div className={`text-[11px] font-semibold uppercase tracking-wide mb-1 ${tone === 'up' ? 'text-[#007A7A]' : 'text-[#B42318]'}`}>
        {title}
      </div>
      <ul className="space-y-1">
        {items.map((t) => (
          <li key={t} className="text-xs text-[#0A1A2F] leading-snug flex gap-1.5">
            <span className="text-[#5C6B7A]">•</span>
            <span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TrendCard({ trend }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const tail = trend.tailwinds || [];
  const head = trend.headwinds || [];
  const limit = 2;
  const more = tail.length > limit || head.length > limit;
  return (
    <div className="rounded-lg border border-gray-200 p-4 bg-white flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <div className="font-semibold text-sm text-[#0A1A2F]">{trend.label || formatCategoryLabel(trend.category)}</div>
        <DirectionPill direction={trend.direction} />
      </div>
      <p className="text-sm text-[#0A1A2F] leading-snug">{trend.summary}</p>
      <div className="grid grid-cols-1 gap-3 mt-1">
        <PointList title={t('mktWorkingInFavour')} tone="up" items={open ? tail : tail.slice(0, limit)} />
        <PointList title={t('mktWorkingAgainst')} tone="down" items={open ? head : head.slice(0, limit)} />
      </div>
      {more && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="self-start inline-flex items-center gap-1 text-xs text-[#007A7A] hover:underline mt-1"
        >
          {open ? <>{t('showLess')} <ChevronUp className="w-3 h-3" /></> : <>{t('showAll')} <ChevronDown className="w-3 h-3" /></>}
        </button>
      )}
    </div>
  );
}

export default function MarketSectorTrends({ tenantId, lens, countryFilter }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMarkets, setShowMarkets] = useState(false);
  const country = countryFilter && countryFilter !== 'all' ? countryFilter : '';

  useEffect(() => {
    let cancelled = false;
    if (!tenantId || !lens) { setLoading(false); setData(null); return undefined; }
    setLoading(true);
    fetchMarketDynamicsCommentary(tenantId, lens, country)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tenantId, lens, country]);

  const markets = useMemo(() => Object.entries(data?.by_country || {}).sort(([a], [b]) => a.localeCompare(b)), [data]);
  const windows = useMemo(() => (data?.windows || []).slice(0, 8), [data]);

  if (loading) return <div className="h-40 bg-gray-100 rounded-md animate-pulse" />;
  if (!data?.available) return null;

  return (
    <Card className="border-[#B3E6E6]">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base text-[#0A1A2F]">
          {t('mktKeySectorTrends')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {data.headline && <p className="text-sm text-[#0A1A2F] leading-relaxed">{data.headline}</p>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(data.trends || []).map((trend) => <TrendCard key={trend.category} trend={trend} />)}
        </div>

        {windows.length > 0 && (
          <div>
            <div className="flex items-center gap-1.5 text-xs font-semibold text-[#0A1A2F] mb-2">
              <CalendarClock className="w-3.5 h-3.5 text-[#5C6B7A]" /> {t('mktUpcomingWindows')}
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
              {windows.map((w, i) => (
                <li key={`${w.country}-${i}`} className="text-xs text-[#0A1A2F] leading-snug">
                  <span className="font-medium">{w.date}</span>
                  <span className="text-[#5C6B7A]"> · {w.country}{w.category ? ` · ${formatCategoryLabel(w.category)}` : ''}</span>
                  <span> — {w.what}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {markets.length > 0 && (
          <div>
            <button
              type="button"
              onClick={() => setShowMarkets((v) => !v)}
              className="inline-flex items-center gap-1 text-xs font-semibold text-[#007A7A] hover:underline"
            >
              {showMarkets ? t('mktHideCountryDetail') : t('mktByCountryN', { n: markets.length })}
              {showMarkets ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            {showMarkets && (
              <div className="mt-2 grid grid-cols-1 lg:grid-cols-2 gap-3">
                {markets.map(([name, notes]) => (
                  <div key={name} className="rounded-md border border-gray-200 p-3">
                    <div className="text-sm font-semibold text-[#0A1A2F] mb-1">{name}</div>
                    <ul className="space-y-1.5">
                      {notes.map((n) => (
                        <li key={n.category} className="text-xs leading-snug">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-[#0A1A2F]">{formatCategoryLabel(n.category)}</span>
                            <DirectionPill direction={n.direction} />
                            {n.barrier && <span className="text-[10px] text-[#B42318] font-medium">{t('mktBarrier')}</span>}
                          </div>
                          <div className="text-[#5C6B7A] mt-0.5">{n.note}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
